import { BattleState, Monster, TurnCommands, TurnResult, CommandType, DistanceType, StanceType } from '../types';
import { AILevel, AIConfig } from './types';
import { getValidCommands } from './commandValidator';
import { getTendencyBySpecies } from './tendencies';
import { getDistanceWeights } from './distanceWeights';
import { selectWeightedCommand, selectDeterministicCommand, selectWeightedCommandPair, selectDeterministicCommandPair, selectTopNWeightedCommandPair, CommandWeightMap, CommandPairWeightMap } from './weightedSelection';
import { getHpModifiers, getStanceResponseModifiers, getReflectorModifiers, getSelfReflectorModifiers } from './situationModifiers';
import { analyzePlayerPattern, getMostFrequentCommand } from './patternAnalyzer';
import { getCounterModifiers } from './counterStrategy';
import { predictStateAfterCommand } from './statePredictor';
import { getOpponentAbilityModifiers } from './opponentAbilityModifiers';
import { getMatchupDistanceModifiers } from './matchupDistanceStrategy';
import { extractSuccessPatterns, getSuccessPatternModifiers } from './successPatternLearning';
import { getMetaReadingModifiers } from './metaReading';
import { getSelfAbilityModifiers } from './selfAbilityModifiers';
import { getAIConfigModifiers, scaleModifiers, getAdjustedFallbackRate } from './aiConfigModifiers';

/**
 * 有効コマンドリストからランダムに1つ選択する
 * @internal
 *
 * 空リストの場合はADVANCEをフォールバックとして返す。
 * 通常 getValidCommands は ADVANCE/RETREAT を必ず含むため空にはならないが、
 * 防御的にフォールバックを用意している。
 */
export function selectSingleCommand(
  validCommands: CommandType[],
  randomFn: () => number
): CommandType {
  if (validCommands.length === 0) {
    return CommandType.ADVANCE;
  }
  const index = Math.floor(randomFn() * validCommands.length);
  return validCommands[index];
}

/**
 * AIレベルに応じてコマンドを選択する
 *
 * @param state 現在のバトル状態
 * @param playerId AIが操作するプレイヤーID
 * @param monster AIが操作するモンスター
 * @param level AIレベル
 * @param randomFn 乱数生成関数（テスト用に注入可能）
 * @param opponentMonster 相手モンスター情報（Lv3以降で使用）
 * @param turnHistory ターン履歴（Lv4で使用、パターン読み用）
 * @returns 選択された2つのコマンド
 */
export function selectCommands(
  state: BattleState,
  playerId: 'player1' | 'player2',
  monster: Monster,
  level: AILevel,
  randomFn: () => number = Math.random,
  opponentMonster?: Monster,
  turnHistory?: TurnResult[],
  aiConfig?: AIConfig
): TurnCommands {
  const configMods = aiConfig ? getAIConfigModifiers(aiConfig) : undefined;

  switch (level) {
    case AILevel.LV1:
      return selectLv1(state, playerId, monster, randomFn, configMods);
    case AILevel.LV2:
      return selectLv2(state, playerId, monster, randomFn, configMods);
    case AILevel.LV3:
      return selectLv3(state, playerId, monster, randomFn, opponentMonster, configMods, aiConfig);
    case AILevel.LV4:
      return selectLv4(state, playerId, monster, randomFn, opponentMonster, turnHistory, configMods, aiConfig);
    case AILevel.LV5:
      return selectLv5(state, playerId, monster, randomFn, opponentMonster, turnHistory, configMods, aiConfig);
    case AILevel.LV6:
      // Lv6 は async 必須（Worker または Promise 返却）。同期版では呼べない
      throw new Error('Lv6 AI must be invoked via selectCommandsAsync()');
    default:
      throw new Error(`Unknown AI level: ${level}`);
  }
}

/**
 * AIレベルに応じてコマンドを選択する（async 版）
 *
 * Lv1-5 は同期版 selectCommands() を Promise.resolve でラップ、
 * Lv6 は selectLv6Async() を呼び出す（Worker 経由 or インライン）。
 *
 * 呼び出し側が「Lv6 を含む可能性のある任意の AI レベル」を扱う場合に使う。
 * 既存の Lv1-5 のみの呼び出し点は selectCommands を引き続き使用可能。
 *
 * @returns 選択されたコマンド（Promise）
 */
export async function selectCommandsAsync(
  state: BattleState,
  playerId: 'player1' | 'player2',
  monster: Monster,
  level: AILevel,
  randomFn: () => number = Math.random,
  opponentMonster?: Monster,
  turnHistory?: TurnResult[],
  aiConfig?: AIConfig,
): Promise<TurnCommands> {
  if (level === AILevel.LV6) {
    if (!opponentMonster) throw new Error('Lv6 AI requires opponentMonster');
    if (turnHistory === undefined) throw new Error('Lv6 AI requires turnHistory');
    if (!aiConfig) throw new Error('Lv6 AI requires aiConfig');
    // 動的import で循環参照を避けつつ lv6Async を呼び出す
    const { selectLv6Async } = await import('./lv6Async');
    return selectLv6Async(state, playerId, monster, opponentMonster, turnHistory, aiConfig);
  }
  // Lv1-5 は同期版を Promise でラップ
  return Promise.resolve(
    selectCommands(state, playerId, monster, level, randomFn, opponentMonster, turnHistory, aiConfig),
  );
}

/**
 * Lv5 AI: ペア評価コンボ + 相手分析 + 成功パターン学習 + メタ読み
 *
 * Lv4の全モディファイアに加え、以下の4つの追加モディファイアを適用:
 * - 相手の能力分析（ステータスに応じた重み調整）
 * - マッチアップ距離戦略（自分vs相手の比較に基づく距離誘導）
 * - 成功パターン学習（直近ターンで成功したコマンドの重み増加）
 * - メタ読み（相手の対策を予測し裏をかく）
 *
 * 選択方式はトップN加重選択（上位3件からスコア比例ランダム）。
 * - Lv3フォールバックなし（常にパターン読みを試みる）
 * - 履歴不足時は新モディファイアの効果がニュートラルに
 *
 * @internal Lv6Core からも呼ばれるため export している（PR2で内部抽出予定）。
 */
export function selectLv5(
  state: BattleState,
  playerId: 'player1' | 'player2',
  monster: Monster,
  randomFn: () => number,
  opponentMonster?: Monster,
  turnHistory?: TurnResult[],
  configMods?: CommandWeightMap,
  aiConfig?: AIConfig
): TurnCommands {
  if (!opponentMonster) {
    throw new Error('Lv5 AI requires opponentMonster');
  }
  if (turnHistory === undefined) {
    throw new Error('Lv5 AI requires turnHistory');
  }

  // 完全予測防止: フォールバック率をunpredictabilityで調整
  const BASE_LV5_FALLBACK_RATE = 0.15;
  const fallbackRate = aiConfig
    ? getAdjustedFallbackRate(BASE_LV5_FALLBACK_RATE, aiConfig.unpredictability)
    : BASE_LV5_FALLBACK_RATE;
  if (randomFn() < fallbackRate) {
    return selectLv3(state, playerId, monster, randomFn, opponentMonster, configMods, aiConfig);
  }

  const opponentId = playerId === 'player1' ? 'player2' : 'player1';

  const speciesTendency = getTendencyBySpecies(monster.id);

  const ownState = state[playerId];
  const oppState = state[opponentId];

  const ownHpRatio = ownState.currentHp / monster.stats.hp;
  const oppHpRatio = opponentMonster.stats.hp > 0
    ? oppState.currentHp / opponentMonster.stats.hp
    : 1.0;
  const rawHpMods = getHpModifiers(ownHpRatio, oppHpRatio);
  const hpMods = aiConfig
    ? scaleModifiers(rawHpMods, 100 - aiConfig.riskTolerance)
    : rawHpMods;
  const reflectorMods = getReflectorModifiers(
    Math.max(0, opponentMonster.reflector.maxReflectCount - oppState.usedReflectCount)
  );
  const selfReflectorMods = getSelfReflectorModifiers(
    Math.max(0, monster.reflector.maxReflectCount - ownState.usedReflectCount)
  );

  // パターン分析（履歴不足時はカウンター無し）
  // patternReading=0-10はパターン分析を無効化（意図的に「読まない」設定が可能）
  const shouldReadPatterns = aiConfig ? aiConfig.patternReading > 10 : true;
  const pattern = shouldReadPatterns ? analyzePlayerPattern(turnHistory, opponentId, 3) : undefined;
  const mostFrequent = pattern ? getMostFrequentCommand(pattern, state.currentDistance) : null;

  const hasCounterData = mostFrequent !== null && mostFrequent.length > 0;
  let counterMods: CommandWeightMap | undefined;
  if (hasCounterData) {
    const targetCommand = mostFrequent!.length === 1
      ? mostFrequent![0]
      : mostFrequent![Math.floor(randomFn() * mostFrequent!.length)];
    const rawCounterMods = getCounterModifiers(targetCommand, state.currentDistance);
    counterMods = aiConfig
      ? scaleModifiers(rawCounterMods, aiConfig.counterTendency)
      : rawCounterMods;
  }

  // 新モディファイア: 相手の能力分析 + マッチアップ距離戦略
  const opponentAbilityMods = getOpponentAbilityModifiers(monster, opponentMonster);
  const matchupDistanceMods = getMatchupDistanceModifiers(monster, opponentMonster);
  const selfAbilityMods = getSelfAbilityModifiers(monster);

  // 新モディファイア: 成功パターン学習 + メタ読み
  const rawSuccessPatternMods = getSuccessPatternModifiers(
    turnHistory, playerId, state.currentDistance
  );
  const successPatternMods = aiConfig
    ? scaleModifiers(rawSuccessPatternMods, aiConfig.adaptability)
    : rawSuccessPatternMods;
  const successPatterns = extractSuccessPatterns(turnHistory, playerId);
  const rawMetaReadingMods = getMetaReadingModifiers(successPatterns, state.currentDistance);
  const metaReadingMods = aiConfig
    ? scaleModifiers(rawMetaReadingMods, aiConfig.metaReading)
    : rawMetaReadingMods;

  // ペア評価: 全(1st, 2nd)ペアをスコア化（追加モディファイア付き）
  const pairScores = buildPairScores(
    state, playerId, monster, opponentMonster,
    speciesTendency, hpMods, reflectorMods, counterMods,
    opponentAbilityMods, matchupDistanceMods, successPatternMods, metaReadingMods,
    selfAbilityMods, selfReflectorMods, configMods
  );

  // トップN加重選択（上位5件からスコア比例ランダム）
  const selected = selectTopNWeightedCommandPair(pairScores, 5, randomFn);
  return {
    first: { type: selected.first },
    second: { type: selected.second },
  };
}

/**
 * Lv4 AI: ペア評価コンボ + パターン読み
 *
 * 全(1st, 2nd)ペアをスコア化し、重み付きランダムで選択。
 * - 直近3ターンの相手コマンドを距離別に集計
 * - 最頻出コマンドに対するカウンターモディファイアを適用
 * - 20%の確率でLv3相当の行動（完全予測防止）
 * - 履歴不足時はカウンター無しのペア評価
 */
function selectLv4(
  state: BattleState,
  playerId: 'player1' | 'player2',
  monster: Monster,
  randomFn: () => number,
  opponentMonster?: Monster,
  turnHistory?: TurnResult[],
  configMods?: CommandWeightMap,
  aiConfig?: AIConfig
): TurnCommands {
  if (!opponentMonster) {
    throw new Error('Lv4 AI requires opponentMonster');
  }
  if (turnHistory === undefined) {
    throw new Error('Lv4 AI requires turnHistory');
  }

  // 完全予測防止: フォールバック率をunpredictabilityで調整
  const BASE_LV4_FALLBACK_RATE = 0.2;
  const fallbackRate = aiConfig
    ? getAdjustedFallbackRate(BASE_LV4_FALLBACK_RATE, aiConfig.unpredictability)
    : BASE_LV4_FALLBACK_RATE;
  if (randomFn() < fallbackRate) {
    return selectLv3(state, playerId, monster, randomFn, opponentMonster, configMods, aiConfig);
  }

  const opponentId = playerId === 'player1' ? 'player2' : 'player1';
  const ownState = state[playerId];
  const oppState = state[opponentId];

  // 共通モディファイア
  const speciesTendency = getTendencyBySpecies(monster.id);
  const ownHpRatio = ownState.currentHp / monster.stats.hp;
  const oppHpRatio = opponentMonster.stats.hp > 0
    ? oppState.currentHp / opponentMonster.stats.hp
    : 1.0;
  const rawHpMods = getHpModifiers(ownHpRatio, oppHpRatio);
  const hpMods = aiConfig
    ? scaleModifiers(rawHpMods, 100 - aiConfig.riskTolerance)
    : rawHpMods;
  const oppRemainingReflect = Math.max(0, opponentMonster.reflector.maxReflectCount - oppState.usedReflectCount);
  const reflectorMods = getReflectorModifiers(oppRemainingReflect);

  // パターン分析 → カウンターモディファイア（データ不足時は undefined）
  const shouldReadPatterns = aiConfig ? aiConfig.patternReading > 10 : true;
  const pattern = shouldReadPatterns ? analyzePlayerPattern(turnHistory, opponentId, 3) : undefined;
  const mostFrequent = pattern ? getMostFrequentCommand(pattern, state.currentDistance) : null;
  const hasCounterData = mostFrequent !== null && mostFrequent.length > 0;
  let counterMods: CommandWeightMap | undefined;
  if (hasCounterData) {
    const targetCommand = mostFrequent!.length === 1
      ? mostFrequent![0]
      : mostFrequent![Math.floor(randomFn() * mostFrequent!.length)];
    const rawCounterMods = getCounterModifiers(targetCommand, state.currentDistance);
    counterMods = aiConfig
      ? scaleModifiers(rawCounterMods, aiConfig.counterTendency)
      : rawCounterMods;
  }

  // ペア評価
  const pairScores = buildPairScores(
    state, playerId, monster, opponentMonster,
    speciesTendency, hpMods, reflectorMods, counterMods,
    undefined, undefined, undefined, undefined,
    undefined, undefined, configMods
  );

  const selected = selectWeightedCommandPair(pairScores, randomFn);
  return {
    first: { type: selected.first },
    second: { type: selected.second },
  };
}

/**
 * 全(1st, 2nd)ペアのスコアマップを構築する
 *
 * 1. 有効な1stコマンドを列挙
 * 2. 各1stについて状態を予測し、予測状態での有効な2ndコマンドを列挙
 * 3. 各ペアのスコア = 1stの重み × 2ndの重み（予測状態）
 */
/**
 * @internal Lv6 lookahead（PR2以降）でムーブオーダリングに使うため export している。
 * 将来的には Lv5/Lv6 共通の評価エンジンとして別ファイルに抽出する予定。
 */
export function buildPairScores(
  state: BattleState,
  playerId: 'player1' | 'player2',
  monster: Monster,
  opponentMonster: Monster,
  speciesTendency: CommandWeightMap,
  hpMods: CommandWeightMap,
  reflectorMods: CommandWeightMap,
  counterMods?: CommandWeightMap,
  opponentAbilityMods?: CommandWeightMap,
  matchupDistanceMods?: CommandWeightMap,
  successPatternMods?: CommandWeightMap,
  metaReadingMods?: CommandWeightMap,
  selfAbilityMods?: CommandWeightMap,
  selfReflectorMods?: CommandWeightMap,
  configMods?: CommandWeightMap
): CommandPairWeightMap {
  const ownState = state[playerId];
  const opponentId = playerId === 'player1' ? 'player2' : 'player1';
  const oppState = state[opponentId];

  const validCommands1st = getValidCommands(state, playerId, monster);
  const distanceWeights1st = getDistanceWeights(state.currentDistance);
  const stanceMods1st = getStanceResponseModifiers(ownState.currentStance, oppState.currentStance);

  const pairScores: CommandPairWeightMap = new Map();

  for (const cmd1st of validCommands1st) {
    // 1stの重みを計算
    const weight1st =
      (speciesTendency[cmd1st] ?? 1.0) *
      (distanceWeights1st[cmd1st] ?? 1.0) *
      (hpMods[cmd1st] ?? 1.0) *
      (stanceMods1st[cmd1st] ?? 1.0) *
      (reflectorMods[cmd1st] ?? 1.0) *
      (counterMods ? (counterMods[cmd1st] ?? 1.0) : 1.0) *
      (opponentAbilityMods ? (opponentAbilityMods[cmd1st] ?? 1.0) : 1.0) *
      (matchupDistanceMods ? (matchupDistanceMods[cmd1st] ?? 1.0) : 1.0) *
      (successPatternMods ? (successPatternMods[cmd1st] ?? 1.0) : 1.0) *
      (metaReadingMods ? (metaReadingMods[cmd1st] ?? 1.0) : 1.0) *
      (selfAbilityMods ? (selfAbilityMods[cmd1st] ?? 1.0) : 1.0) *
      (selfReflectorMods ? (selfReflectorMods[cmd1st] ?? 1.0) : 1.0) *
      (configMods ? (configMods[cmd1st] ?? 1.0) : 1.0);

    if (weight1st <= 0) continue;

    // 1st実行後の状態を予測
    const { predictedDistance, predictedStance } = predictStateAfterCommand(
      state.currentDistance,
      ownState.currentStance,
      cmd1st
    );

    // 予測状態でのBattleStateを構築
    const predictedState = buildPredictedState(state, playerId, predictedDistance, cmd1st, monster);
    const validCommands2nd = getValidCommands(predictedState, playerId, monster);

    const distanceWeights2nd = getDistanceWeights(predictedDistance);
    const stanceMods2nd = getStanceResponseModifiers(predictedStance, oppState.currentStance);

    for (const cmd2nd of validCommands2nd) {
      const weight2nd =
        (speciesTendency[cmd2nd] ?? 1.0) *
        (distanceWeights2nd[cmd2nd] ?? 1.0) *
        (hpMods[cmd2nd] ?? 1.0) *
        (stanceMods2nd[cmd2nd] ?? 1.0) *
        (reflectorMods[cmd2nd] ?? 1.0) *
        (counterMods ? (counterMods[cmd2nd] ?? 1.0) : 1.0) *
        (opponentAbilityMods ? (opponentAbilityMods[cmd2nd] ?? 1.0) : 1.0) *
        (matchupDistanceMods ? (matchupDistanceMods[cmd2nd] ?? 1.0) : 1.0) *
        (successPatternMods ? (successPatternMods[cmd2nd] ?? 1.0) : 1.0) *
        (metaReadingMods ? (metaReadingMods[cmd2nd] ?? 1.0) : 1.0) *
        (selfAbilityMods ? (selfAbilityMods[cmd2nd] ?? 1.0) : 1.0) *
        (selfReflectorMods ? (selfReflectorMods[cmd2nd] ?? 1.0) : 1.0) *
        (configMods ? (configMods[cmd2nd] ?? 1.0) : 1.0);

      if (weight2nd <= 0) continue;

      const pairScore = weight1st * weight2nd;
      const key = `${cmd1st}:${cmd2nd}`;
      pairScores.set(key, { first: cmd1st, second: cmd2nd, score: pairScore });
    }
  }

  return pairScores;
}

/**
 * Lv3 AI: Lv2 + 状況考慮 + 順次予測コンボ
 *
 * Lv2の距離＋種族傾向に加え、以下の状況モディファイアを適用:
 * - HP状況（自分/相手のHP割合）
 * - 相手スタンスへの対応（カウンター行動）
 * - 相手リフレクター残数（特殊攻撃の頻度調整）
 *
 * 順次予測コンボ:
 * 1stを選択後、1stコマンドによる距離・スタンス変化を予測し、
 * 予測状態で2ndの重みを再計算して選択する。
 */
function selectLv3(
  state: BattleState,
  playerId: 'player1' | 'player2',
  monster: Monster,
  randomFn: () => number,
  opponentMonster?: Monster,
  configMods?: CommandWeightMap,
  aiConfig?: AIConfig
): TurnCommands {
  const ownState = state[playerId];
  const opponentId = playerId === 'player1' ? 'player2' : 'player1';
  const oppState = state[opponentId];

  // 共通モディファイア（距離・スタンス非依存）
  const speciesTendency = getTendencyBySpecies(monster.id);
  const ownHpRatio = ownState.currentHp / monster.stats.hp;
  const oppMaxHp = opponentMonster ? opponentMonster.stats.hp : oppState.currentHp;
  const oppHpRatio = oppMaxHp > 0 ? oppState.currentHp / oppMaxHp : 1.0;
  const rawHpMods = getHpModifiers(ownHpRatio, oppHpRatio);
  const hpMods = aiConfig
    ? scaleModifiers(rawHpMods, 100 - aiConfig.riskTolerance)
    : rawHpMods;
  const oppMaxReflect = opponentMonster ? opponentMonster.reflector.maxReflectCount : 2;
  const oppRemainingReflect = Math.max(0, oppMaxReflect - oppState.usedReflectCount);
  const reflectorMods = getReflectorModifiers(oppRemainingReflect);

  // --- 1st選択（現在状態） ---
  const validCommands1st = getValidCommands(state, playerId, monster);
  const distanceWeights1st = getDistanceWeights(state.currentDistance);
  const stanceMods1st = getStanceResponseModifiers(ownState.currentStance, oppState.currentStance);

  const weights1st: CommandWeightMap = {};
  for (const cmd of validCommands1st) {
    weights1st[cmd] =
      (speciesTendency[cmd] ?? 1.0) *
      (distanceWeights1st[cmd] ?? 1.0) *
      (hpMods[cmd] ?? 1.0) *
      (stanceMods1st[cmd] ?? 1.0) *
      (reflectorMods[cmd] ?? 1.0) *
      (configMods ? (configMods[cmd] ?? 1.0) : 1.0);
  }

  const firstCmd = selectWeightedCommand(weights1st, randomFn);

  // --- 2nd選択（予測状態） ---
  const { predictedDistance, predictedStance } = predictStateAfterCommand(
    state.currentDistance,
    ownState.currentStance,
    firstCmd
  );

  // 予測状態での有効コマンドを計算
  const predictedState = buildPredictedState(state, playerId, predictedDistance, firstCmd, monster);
  const validCommands2nd = getValidCommands(predictedState, playerId, monster);

  const distanceWeights2nd = getDistanceWeights(predictedDistance);
  const stanceMods2nd = getStanceResponseModifiers(predictedStance, oppState.currentStance);

  const weights2nd: CommandWeightMap = {};
  for (const cmd of validCommands2nd) {
    weights2nd[cmd] =
      (speciesTendency[cmd] ?? 1.0) *
      (distanceWeights2nd[cmd] ?? 1.0) *
      (hpMods[cmd] ?? 1.0) *
      (stanceMods2nd[cmd] ?? 1.0) *
      (reflectorMods[cmd] ?? 1.0) *
      (configMods ? (configMods[cmd] ?? 1.0) : 1.0);
  }

  const secondCmd = selectWeightedCommand(weights2nd, randomFn);

  return {
    first: { type: firstCmd },
    second: { type: secondCmd },
  };
}

/**
 * 1stコマンド実行後の予測状態でBattleStateを構築する
 *
 * getValidCommands が距離やリフレクター残数を参照するため、
 * 予測距離と予測リフレクター使用状況を反映したBattleStateを生成する。
 */
/**
 * @internal Lv6 lookahead で next state 構築に再利用するため export している。
 */
export function buildPredictedState(
  originalState: BattleState,
  playerId: 'player1' | 'player2',
  predictedDistance: DistanceType,
  firstCmd: CommandType,
  monster: Monster
): BattleState {
  const playerState = originalState[playerId];
  const predictedUsedReflect = firstCmd === CommandType.REFLECTOR
    ? playerState.usedReflectCount + 1
    : playerState.usedReflectCount;

  return {
    ...originalState,
    currentDistance: predictedDistance,
    [playerId]: {
      ...playerState,
      usedReflectCount: predictedUsedReflect,
    },
  };
}

/**
 * Lv2 AI: 距離に応じた基本行動 + 種族傾向
 *
 * 種族傾向の重みと距離別モディファイアを掛け合わせて、
 * 重み付きランダム選択を行う。
 *
 * 制約: STANCE_A/Bの重みは現在のスタンスに関わらず固定。
 * Lv3以降でスタンス遷移先の適切さを考慮する。
 */
function selectLv2(
  state: BattleState,
  playerId: 'player1' | 'player2',
  monster: Monster,
  randomFn: () => number,
  configMods?: CommandWeightMap
): TurnCommands {
  const validCommands = getValidCommands(state, playerId, monster);
  const speciesTendency = getTendencyBySpecies(monster.id);
  const distanceWeights = getDistanceWeights(state.currentDistance);

  const combinedWeights: CommandWeightMap = {};
  for (const cmd of validCommands) {
    combinedWeights[cmd] =
      speciesTendency[cmd] *
      distanceWeights[cmd] *
      (configMods ? (configMods[cmd] ?? 1.0) : 1.0);
  }

  return {
    first: { type: selectWeightedCommand(combinedWeights, randomFn) },
    second: { type: selectWeightedCommand(combinedWeights, randomFn) },
  };
}

/**
 * Lv1 AI: ほぼランダム行動。重み付けなし。
 * 有効なコマンドから均等確率でランダムに選択する。
 */
function selectLv1(
  state: BattleState,
  playerId: 'player1' | 'player2',
  monster: Monster,
  randomFn: () => number,
  configMods?: CommandWeightMap
): TurnCommands {
  const validCommands = getValidCommands(state, playerId, monster);

  if (configMods) {
    // AIConfig適用時は重み付き選択（傾向はあるが状況判断はしない）
    const weights: CommandWeightMap = {};
    for (const cmd of validCommands) {
      weights[cmd] = configMods[cmd] ?? 1.0;
    }
    return {
      first: { type: selectWeightedCommand(weights, randomFn) },
      second: { type: selectWeightedCommand(weights, randomFn) },
    };
  }

  return {
    first: { type: selectSingleCommand(validCommands, randomFn) },
    second: { type: selectSingleCommand(validCommands, randomFn) },
  };
}
